import React, { useContext, useCallback, useState, useMemo, useRef } from 'react'
import useToggle from 'hooks/useToggle'
import useCruiseSearchCount from 'hooks/Cruise/useCruiseSearchCount'
import useCruiseSearchFacets from 'hooks/Cruise/useCruiseSearchFacets'
import { GlobalSearchStateContext, GlobalSearchDispatchContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter from 'components/Luxkit/Modal/ModalFooter'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import IconButton from 'components/Luxkit/Button/IconButton'
import CruiseLocationContent from './CruiseLocationContent'
import CruiseLocationFooter from './CruiseLocationFooter'

const SET_SEARCH_ITEMS = GlobalSearchStateActions.SET_SEARCH_ITEMS

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void;
}

function CruiseLocationMobileInput({ onChange }: Props) {
  const inputTriggerRef = useRef<HTMLButtonElement>(null)
  const inputSearchRef = useRef<HTMLInputElement>(null)

  const { searchItems } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const [searchTerm, setSearchTerm] = useState('')
  const [isAllSelected, setIsAllSelected] = useState(false)
  const [isOpen, toggle,, close] = useToggle(false)

  const [facets] = useCruiseSearchFacets({ facetTypes: ['destination_ports'] })

  const locations = useMemo(() => {
    if (!searchTerm) return facets.filter(({ isTrend }) => isTrend)
    return facets.filter(({ name }) => name.toLowerCase().includes(searchTerm.toLowerCase()))
  }, [searchTerm, facets])

  const inputValue = useMemo(() => {
    if (isOpen) return searchTerm
    if (searchItems.length > 1) {
      const names = searchItems.slice(0, 1).map((item) => item.format.mainText).join(', ')
      return `${names} + ${searchItems.length - 1}`
    }
    return searchItems.map((item) => item.format.mainText).join(', ')
  }, [searchItems, isOpen, searchTerm])

  const [searchCount, fetchingSearchCount] = useCruiseSearchCount({
    destinationIds: searchItems.map(({ value }) => value),
  })

  const handleOnApply = useCallback(() => {
    onChange({ searchItems })
    close()
  }, [onChange, searchItems, close])

  const handleOnClear = useCallback(() => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setSearchTerm('')
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleOnChange = useCallback((searchItems: App.SearchItem[]) => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems })
    setIsAllSelected(false)
  }, [searchDispatch])

  const handleSelectAll = useCallback(() => {
    searchDispatch({ type: SET_SEARCH_ITEMS, searchItems: [] })
    setIsAllSelected(!isAllSelected)
  }, [searchDispatch, isAllSelected])

  const handlerSearchTerm = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }, [])

  const handlerClearSearchTerm = useCallback(() => setSearchTerm(''), [])

  return <>
    <SearchFormField
      onClick={toggle}
      ref={inputTriggerRef}
      label="Cruising to"
      placeholder="All destinations"
      value={inputValue}
    />

    <ModalBase
      mode="drawer"
      isOpen={isOpen}
      onClose={toggle}
    >
      <ModalHeader
        title="Cruising to"
        onCloseButtonClick={toggle}
      />
      <ModalBody>
        <ModalContent>
          <TextInputOnly
            ref={inputSearchRef}
            noValidationSpacing
            placeholder="Search destinations"
            defaultValue={inputValue}
            onChange={handlerSearchTerm}
            endIcon={searchTerm.length > 0 ?
              <IconButton kind="tertiary" onClick={handlerClearSearchTerm}><LineTimesIcon /></IconButton> :
              <LineSearchIcon />
            }
          />

          <CruiseLocationContent
            locations={locations}
            searchTerm={searchTerm}
            searchItems={searchItems}
            onChange={handleOnChange}
            onSelectAll={handleSelectAll}
            inputSearchRef={inputSearchRef}
            isAllSelected={isAllSelected}
            isMobileMode
          />
        </ModalContent>
      </ModalBody>
      <ModalFooter>
        <CruiseLocationFooter
          searchCount={searchCount}
          fetchingSearchCount={fetchingSearchCount}
          onClear={handleOnClear}
          onApply={handleOnApply}
        />
      </ModalFooter>
    </ModalBase>
  </>
}

export default CruiseLocationMobileInput
